import React from 'react';
import { DeviceIDBadge, ComplaintIDBadge } from '../../../shared/components/common/Badges';
import { formatDepartmentWithIcon } from '../../admin/utils/departmentUtils';

const META_SEP = ' · ';

function buildDeptPhysicalText(alert) {
  const parts = [];
  if (alert.department_name) {
    parts.push(formatDepartmentWithIcon(alert.department_name, alert.department_icon));
  }
  if (alert.physical_location) {
    parts.push(alert.physical_location);
  }
  return parts.join(META_SEP);
}

function SubjectLocationLine({ locationName }) {
  if (!locationName) return null;
  return (
    <div className="subject-location-line" title={locationName}>
      📍 {locationName}
    </div>
  );
}

function SubjectDeptPhysicalLine({ alert }) {
  const text = buildDeptPhysicalText(alert);
  if (!text) return null;
  return (
    <div className="subject-detail-line" title={text}>
      {text}
    </div>
  );
}

/**
 * Three-row subject layout: location → primary identifiers → dept / physical.
 */
function SubjectThreeRowLayout({ alert, primaryRow }) {
  const hasLocation = Boolean(alert.location_name);
  const hasPrimary = Boolean(primaryRow);
  const hasDetail = Boolean(alert.department_name || alert.physical_location);

  if (!hasLocation && !hasPrimary && !hasDetail) return null;

  return (
    <div className="subject-stack subject-stack-three-row">
      <SubjectLocationLine locationName={alert.location_name} />
      {primaryRow}
      <SubjectDeptPhysicalLine alert={alert} />
    </div>
  );
}

/**
 * Polymorphic subject cell for alerts table (device, complaint, location, user, org).
 */
export function SubjectContext({ alert, onViewDevice, onViewComplaint }) {
  const subjectType = alert?.subject_type;

  const handleDeviceClick = (e) => {
    e.stopPropagation();
    if (onViewDevice) onViewDevice(alert);
  };

  const handleComplaintClick = (e) => {
    e.stopPropagation();
    if (onViewComplaint) onViewComplaint(alert);
  };

  if (subjectType === 'device') {
    const hasPrimary = alert.device_name || alert.device_identifier;
    if (!hasPrimary && !alert.location_name && !buildDeptPhysicalText(alert)) return null;

    const primaryRow = hasPrimary ? (
      <div className="subject-primary-row">
        {alert.device_name && (
          <span className="subject-primary-name">{alert.device_name}</span>
        )}
        {alert.device_identifier && (
          <DeviceIDBadge
            deviceId={alert.device_identifier}
            size="small"
            clickable={true}
            onClick={handleDeviceClick}
          />
        )}
      </div>
    ) : null;

    return <SubjectThreeRowLayout alert={alert} primaryRow={primaryRow} />;
  }

  if (subjectType === 'complaint') {
    const hasPrimary =
      alert.complaint_identifier || alert.device_name || alert.device_identifier;
    if (!hasPrimary && !alert.location_name && !buildDeptPhysicalText(alert)) return null;

    const primaryRow = hasPrimary ? (
      <div className="subject-primary-row">
        {alert.complaint_identifier && (
          <ComplaintIDBadge
            complaintId={alert.complaint_identifier}
            size="small"
            clickable={true}
            onClick={handleComplaintClick}
            title={alert.complaint_title || alert.complaint_identifier}
          />
        )}
        {alert.device_name && (
          <span className="subject-primary-name">{alert.device_name}</span>
        )}
        {alert.device_identifier && (
          <DeviceIDBadge
            deviceId={alert.device_identifier}
            size="small"
            clickable={true}
            onClick={handleDeviceClick}
          />
        )}
      </div>
    ) : null;

    return <SubjectThreeRowLayout alert={alert} primaryRow={primaryRow} />;
  }

  if (subjectType === 'location') {
    if (!alert.location_name && !buildDeptPhysicalText(alert)) return null;

    return <SubjectThreeRowLayout alert={alert} primaryRow={null} />;
  }

  if (subjectType === 'user' || subjectType === 'organization') {
    const label = alert.subject_display_name;
    if (!label && !alert.location_name && !buildDeptPhysicalText(alert)) return null;

    const primaryRow = label ? (
      <div className="subject-primary-row">
        <span className="subject-primary-name">{label}</span>
      </div>
    ) : null;

    return <SubjectThreeRowLayout alert={alert} primaryRow={primaryRow} />;
  }

  return null;
}

export default SubjectContext;
